import { SFXAudio } from '@/config/AudioConfig'
import AudioEngine from '@/webgl/audio/AudioEngine'
import gui from '@/webgl/dev/gui'
import { BonusEntity, BonusType, createBonusEntity } from '@/webgl/runner/entities/BonusEntity'
import {
  CollectStatus,
  getCollectibleSwitchFromBonus as getCollectibleSwitchFromBonus,
} from '@/webgl/runner/entities/CollectibleEntity'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameWorld from '@/webgl/runner/GameWorld'
import { ISystem } from '@/webgl/runner/interfaces/ISystem'
import { splitCompletionBlock } from '@/webgl/runner/scenarios/SequenceCompletion'
import PostprocessSystem from '@/webgl/runner/systems/PostprocessSystem'

export default class BonusSystem implements ISystem {
  constructor(readonly world: GameWorld) {
    const fld = gui.folder('Bonuses')
    fld.btn('Add Relax', () => this.world.addBonus(createBonusEntity(BonusType.Relax)))
    fld.btn('Add Focus', () => this.world.addBonus(createBonusEntity(BonusType.Focus)))
    fld.btn('Add Visual Chart', () => this.world.addBonus(createBonusEntity(BonusType.Chart)))
    fld.btn('Add Switch', () => this.world.addBonus(createBonusEntity(BonusType.Switch)))
    fld.btn('Add Helmet', () => this.world.addBonus(createBonusEntity(BonusType.Helmet)))
    fld.btn('Add Pixelate', () => this.world.addBonus(createBonusEntity(BonusType.Pixelated)))
    fld.btn('Add Altered', () => this.world.addBonus(createBonusEntity(BonusType.AltPrice)))
    fld.btn('Add Phone', () => this.world.addBonus(createBonusEntity(BonusType.PhoneCalculator)))
    fld.btn('Add Freeze', () => this.world.addBonus(createBonusEntity(BonusType.Freeze)))
    fld.btn('Add Confusion', () => this.world.addBonus(createBonusEntity(BonusType.Confusion)))
    fld.btn('Add Price Coins', () => this.world.addBonus(createBonusEntity(BonusType.PriceCoins)))
  }

  update(time: TimeEntity): void {
    const bonuses = this.world.bonuses
    const destroyable: BonusEntity[] = []
    for (const bonus of bonuses) {
      if (bonus.lifetime == 0 && !bonus.applied) {
        this.applyAddedEffect(bonus)
      }

      bonus.lifetime += time.deltaTime

      if (bonus.duration < 0) continue
      if (bonus.lifetime >= bonus.duration) {
        this.applyDestroyEffect(bonus)
        destroyable.push(bonus)
      }
    }

    for (const bonus of destroyable) {
      this.world.destroyBonus(bonus)
    }
  }

  applyAddedEffect(bonus: BonusEntity): void {
    bonus.applied = true

    if (bonus.type == BonusType.Pixelated) {
      this.world.player.grabDisabled = true
      AudioEngine.play(SFXAudio.Pixels)
      this.world.systems.get<PostprocessSystem>(PostprocessSystem).pixelateAnimation()
    }

    if (bonus.type == BonusType.Freeze) {
      this.world.systems.get<PostprocessSystem>(PostprocessSystem).enableFreeze()
      this.world.shopping?.setFreeze(true)
    }

    if (bonus.type == BonusType.PhoneCalculator) {
      this.world.destroyBonus(bonus)
      const altered = this.world.getActiveBonuses([BonusType.AltPrice])
      if (altered.length > 0) {
        this.world.destroyBonus(altered[0])
      }
      for (const collectible of this.world.collectibles) {
        collectible.blur = 0
      }
    }

    if (bonus.type == BonusType.Focus) {
      const altered = this.world.getActiveBonuses([BonusType.Helmet])
      if (altered.length > 0) {
        this.world.state.alterJauge(-0.5)
        this.world.destroyBonus(altered[0])
      }
    }

    if (bonus.type == BonusType.Lego) {
      const completion = this.world.state.value.completion

      const blocks = [...completion.blocks]
      const newBlocks = []
      let newCurrentBlock = completion.currentBlock

      for (let i = 0; i < blocks.length; i++) {
        const block = blocks[i]
        const split = splitCompletionBlock(block)
        newBlocks.push(...split)
        if (i == completion.currentBlock) {
          newCurrentBlock = i * 2 + (split[0].status != CollectStatus.Neutral ? 1 : 0)
        }
      }

      completion.blocks = newBlocks
      completion.currentBlock = newCurrentBlock
      this.world.state.setCompletion(completion)
    }

    if (bonus.type == BonusType.Mirror) {
      for (const collectible of this.world.collectibles) {
        collectible.mirrored = true
      }
    }

    if (bonus.type == BonusType.Chart) {
      this.world.state.alterJauge(-1)
    }

    if (getCollectibleSwitchFromBonus(bonus.type) !== null) {
      this.applySwitchBonus(bonus)
    }
  }

  applySwitchBonus(bonus: BonusEntity) {
    const cswitch = getCollectibleSwitchFromBonus(bonus.type, 0.0)
    for (const collectible of this.world.collectibles) {
      collectible.switch = collectible.data.canSwitch ? cswitch : null
    }

    const state = this.world.state.value

    if (bonus.type == BonusType.Chart && state.completion.view) {
      const view = state.completion.view
      const suffix = '_' + cswitch.suffix

      const matches = view.template.match(/{{(.*?)}}/g)
      const values = matches.map((m) => m.replace('{{', '').replace('}}', ''))

      for (const value of values) {
        if (value == 'goal') continue
        const replacement = `{{${value + suffix}}}`
        view.template = view.template.replace(`{{${value}}}`, replacement)
        view.suffix = suffix
      }

      this.world.state.updateCompletionView(view)
    }
  }

  applyDestroyEffect(bonus: BonusEntity): void {
    const postprocess = this.world.systems.get<PostprocessSystem>(PostprocessSystem)
    switch (bonus.type) {
      case BonusType.Relax:
        this.world.state.alterJauge(-1.0)
        break

      case BonusType.Pixelated:
        postprocess.releasePixelate()
        this.world.player.grabDisabled = false
        break
    }
  }

  dispose(): void {}
}
